"use client";

import axios from "axios";
import { signInWithPopup, type AuthError } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import {
  applyAuthLoginEnvelope,
  getEmailVerificationPathIfNeeded,
  navigateAfterLogin,
} from "@/features/auth/lib/applyAuthLoginEnvelope";
import { auth, appleProvider, googleProvider } from "@/lib/firebase";
import { useFirebaseLoginMutation } from "@/hooks/useUser";
import { useAuthStore } from "@/store/auth.store";

function socialErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    return String(
      (err.response?.data as { message?: string })?.message ??
        err.message ??
        "Request failed.",
    );
  }
  if (err && typeof err === "object" && "message" in err) {
    const ae = err as AuthError;
    if (typeof ae.message === "string" && ae.message) return ae.message;
  }
  return "Sign-in failed.";
}

type Options = {
  setFormError: (message: string) => void;
  clearFormError: () => void;
};

export function useSocialLogin({ setFormError, clearFormError }: Options) {
  const router = useRouter();
  const setUser = useAuthStore((s) => s.setUser);
  const firebaseLoginMutation = useFirebaseLoginMutation();
  const [flowPending, setFlowPending] = useState(false);

  const exchangeIdTokenWithBackend = useCallback(
    (idToken: string) => {
      firebaseLoginMutation.mutate(
        { idToken },
        {
          onSuccess: (resBody) => {
            const verifyPath = getEmailVerificationPathIfNeeded(resBody);
            if (verifyPath) {
              router.push(verifyPath);
              return;
            }

            const result = applyAuthLoginEnvelope(resBody);
            if (!result.ok) {
              setFormError(result.message);
              return;
            }
            setUser(result.user);
            navigateAfterLogin(result.user, router);
          },
          onError: (e) => {
            setFormError(socialErrorMessage(e));
          },
          onSettled: () => {
            setFlowPending(false);
          },
        },
      );
    },
    [firebaseLoginMutation, router, setUser, setFormError],
  );

  const signInWithGoogle = useCallback(async () => {
    clearFormError();
    setFlowPending(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const idToken = await cred.user.getIdToken();
      exchangeIdTokenWithBackend(idToken);
    } catch (e) {
      setFormError(socialErrorMessage(e));
      setFlowPending(false);
    }
  }, [clearFormError, exchangeIdTokenWithBackend, setFormError]);

  const signInWithApple = useCallback(async () => {
    clearFormError();
    setFlowPending(true);
    try {
      const cred = await signInWithPopup(auth, appleProvider);
      const idToken = await cred.user.getIdToken();
      exchangeIdTokenWithBackend(idToken);
    } catch (e) {
      setFormError(socialErrorMessage(e));
      setFlowPending(false);
    }
  }, [clearFormError, exchangeIdTokenWithBackend, setFormError]);

  const isSocialLoading = flowPending || firebaseLoginMutation.isPending;

  return {
    signInWithGoogle,
    signInWithApple,
    isSocialLoading,
  };
}
